import {getFunctions, httpsCallable} from "firebase/functions";
import {app} from "../firebase";
// ===============================
// AdminPanel.js
// Hotel-Wise Booking Points + Spin Points
// ===============================

import React, { useEffect, useMemo, useState } from "react";
import { auth, db } from "../firebase";

import {
  collection,
  deleteDoc,
  doc,
  getDocs,
  query,
  updateDoc,
  where,
} from "firebase/firestore";

import { onAuthStateChanged } from "firebase/auth";

const ADMIN_UID = "imXv7XiyoYSxJRg9kLzo7jhrbJI3";

function AdminPanel() {
  const [bookings, setBookings] = useState([]);
  const [users, setUsers] = useState([]);
  const [hotels, setHotels] = useState([]);

  const [searchPhone, setSearchPhone] = useState("");
  const [searchedUser, setSearchedUser] = useState(null);

  const [authReady, setAuthReady] = useState(false);
  const [isAdmin, setIsAdmin] = useState(false);

  const [loading, setLoading] = useState(true);
  const [savingUser, setSavingUser] = useState(false);

  // ===============================
  // AUTH CHECK
  // ===============================

  useEffect(() => {
    const unsubscribe = onAuthStateChanged(auth, (user) => {
      setAuthReady(true);

      setIsAdmin(
        !!user && user.uid === ADMIN_UID
      );
    });

    return () => unsubscribe();
  }, []);

  useEffect(() => {
    if (!authReady) return;

    if (!isAdmin) {
      setLoading(false);
      return;
    }

    loadAllData();
  }, [authReady, isAdmin]);

  // ===============================
  // LOAD DATA
  // ===============================

  const loadAllData = async () => {
    setLoading(true);

    try {
      await Promise.all([
        fetchBookings(),
        fetchUsers(),
        fetchHotels(),
      ]);
    } catch (error) {
      console.error("Admin data load error:", error);

      alert(
        "Admin data load error: " +
          (error?.message || error)
      );
    } finally {
      setLoading(false);
    }
  };

  const fetchBookings = async () => {
    const snapshot = await getDocs(
      collection(db, "bookings")
    );

    const list = snapshot.docs.map((bookingDoc) => ({
      id: bookingDoc.id,
      ...bookingDoc.data(),
    }));

    list.sort((a, b) => {
      const firstTime = a?.createdAt?.toMillis
        ? a.createdAt.toMillis()
        : 0;

      const secondTime = b?.createdAt?.toMillis
        ? b.createdAt.toMillis()
        : 0;

      return secondTime - firstTime;
    });

    setBookings(list);
  };

  const fetchUsers = async () => {
    const snapshot = await getDocs(
      collection(db, "users")
    );

    const list = snapshot.docs.map((userDoc) => ({
      id: userDoc.id,
      ...userDoc.data(),
    }));

    list.sort((a, b) =>
      String(a.name || "").localeCompare(
        String(b.name || "")
      )
    );

    setUsers(list);
  };

  const fetchHotels = async () => {
    const snapshot = await getDocs(
      collection(db, "hotels")
    );

    const list = snapshot.docs.map((hotelDoc) => ({
      id: hotelDoc.id,
      ...hotelDoc.data(),
    }));

    list.sort(
      (a, b) =>
        Number(a.sort ?? 9999) -
        Number(b.sort ?? 9999)
    );

    setHotels(list);
  };

  // ===============================
  // HELPERS
  // ===============================

  const getHotelName = (hotelId) => {
    const hotel = hotels.find(
      (item) => item.id === hotelId
    );

    return hotel?.name || hotelId;
  };

  const getBookingFinalPrice = (booking) => {
    return Number(
      booking?.finalPrice ??
        booking?.price ??
        0
    );
  };

  const isHotelFreeBooking = (booking) => {
    return (
      booking?.isHotelFree === true ||
      booking?.paymentType ===
        "free_hotel_loyalty"
    );
  };

  // ===============================
  // COUNTS
  // ===============================

  const pendingCount = useMemo(
    () =>
      bookings.filter(
        (booking) =>
          booking.status === "pending"
      ).length,
    [bookings]
  );

  const paidRequestedCount = useMemo(
    () =>
      bookings.filter(
        (booking) =>
          booking.status ===
          "paid_requested"
      ).length,
    [bookings]
  );

  const checkedInCount = useMemo(
    () =>
      bookings.filter(
        (booking) =>
          booking.checkedIn === true ||
          booking.status === "checked_in"
      ).length,
    [bookings]
  );

  const freeStayCount = useMemo(
    () =>
      bookings.filter((booking) =>
        isHotelFreeBooking(booking)
      ).length,
    [bookings]
  );

  // ===============================
  // BOOKING ACTIONS
  // ===============================

  const confirmBooking = async (booking) => {
    const confirmed = window.confirm(
      `Booking confirm karni hai?\n\n` +
        `Name: ${booking.name || "-"}\n` +
        `Hotel: ${booking.hotel || "-"}\n` +
        `Final Price: ₹${getBookingFinalPrice(
          booking
        )}`
    );

    if (!confirmed) return;

    try {
      await updateDoc(
        doc(db, "bookings", booking.id),
        {
          status: "done",
          confirmedAt: new Date(),
        }
      );

      alert("Booking confirmed ✅");

      await fetchBookings();
    } catch (error) {
      console.error(
        "Booking confirm error:",
        error
      );

      alert(
        "Booking confirm error: " +
          (error?.message || error)
      );
    }
  };

  const confirmPayment = async (booking) => {
    const confirmed = window.confirm(
      `Payment confirm karke booking DONE karni hai?\n\n` +
        `Name: ${booking.name || "-"}\n` +
        `Hotel: ${booking.hotel || "-"}\n` +
        `Amount: ₹${
          booking.paidAmount ||
          getBookingFinalPrice(booking)
        }`
    );

    if (!confirmed) return;

    try {
      await updateDoc(
        doc(db, "bookings", booking.id),
        {
          status: "done",
          paymentVerified: true,
          paymentVerifiedAt: new Date(),
        }
      );

      alert(
        "Payment verified and booking confirmed ✅"
      );

      await fetchBookings();
    } catch (error) {
      console.error(
        "Payment confirm error:",
        error
      );

      alert(
        "Payment confirm error: " +
          (error?.message || error)
      );
    }
  };

  const deleteBooking = async (bookingId) => {
    const confirmed = window.confirm(
      "Is booking ko delete karna hai?"
    );

    if (!confirmed) return;

    try {
      await deleteDoc(
        doc(db, "bookings", bookingId)
      );

      alert("Booking deleted ✅");

      setBookings((previousBookings) =>
        previousBookings.filter(
          (booking) =>
            booking.id !== bookingId
        )
      );
    } catch (error) {
      console.error(
        "Booking delete error:",
        error
      );

      alert(
        "Booking delete error: " +
          (error?.message || error)
      );
    }
  };

  // ===============================
  // USER SEARCH
  // ===============================

  const handleSearch = async () => {
    const phone = String(searchPhone || "")
      .replace(/\D/g, "")
      .trim();

    if (!phone) {
      return alert(
        "Phone number enter karo."
      );
    }

    try {
      const userQuery = query(
        collection(db, "users"),
        where("phoneNumber", "==", phone)
      );

      const snapshot = await getDocs(userQuery);

      if (snapshot.empty) {
        setSearchedUser(null);
        return alert("User not found.");
      }

      const userDocument = snapshot.docs[0];

      setSearchedUser({
        id: userDocument.id,
        ...userDocument.data(),

        spinPoints: Number(
          userDocument.data().spinPoints || 0
        ),

        hotelBookingPoints: {
          ...(userDocument.data()
            .hotelBookingPoints || {}),
        },
      });
    } catch (error) {
      console.error(
        "User search error:",
        error
      );

      alert(
        "User search error: " +
          (error?.message || error)
      );
    }
  };

  // ===============================
  // USER REWARD UPDATE
  // ===============================

  const updateUserRewards = async () => {
    if (!searchedUser?.id) return;

    const spinPoints = Number(
      searchedUser.spinPoints || 0
    );

    if (spinPoints < 0) {
      return alert(
        "Spin Points negative nahi ho sakte."
      );
    }

    const cleanHotelBookingPoints = {};

    Object.entries(
      searchedUser.hotelBookingPoints || {}
    ).forEach(([hotelId, points]) => {
      const safePoints = Number(points || 0);

      cleanHotelBookingPoints[hotelId] =
        safePoints < 0 ? 0 : safePoints;
    });

    setSavingUser(true);

    try {
      await updateDoc(
        doc(db, "users", searchedUser.id),
        {
          spinPoints,
          hotelBookingPoints:
            cleanHotelBookingPoints,
          rewardsUpdatedAt: new Date(),
        }
      );

      alert(
        "User reward points updated ✅"
      );

      setSearchedUser((previousUser) => ({
        ...previousUser,
        spinPoints,
        hotelBookingPoints:
          cleanHotelBookingPoints,
      }));

      await fetchUsers();
    } catch (error) {
      console.error(
        "Rewards update error:",
        error
      );

      alert(
        "Rewards update error: " +
          (error?.message || error)
      );
    } finally {
      setSavingUser(false);
    }
  };

  const updateSearchedHotelPoints = (
    hotelId,
    value
  ) => {
    const safeValue = Math.max(
      0,
      Number(value || 0)
    );

    setSearchedUser((previousUser) => ({
      ...previousUser,

      hotelBookingPoints: {
        ...(previousUser.hotelBookingPoints ||
          {}),

        [hotelId]: safeValue,
      },
    }));
  };

  const addHotelPointsRow = () => {
    if (!hotels.length) {
      return alert(
        "Hotels load nahi hue."
      );
    }

    const unusedHotel = hotels.find(
      (hotel) =>
        !Object.prototype.hasOwnProperty.call(
          searchedUser.hotelBookingPoints ||
            {},
          hotel.id
        )
    );

    if (!unusedHotel) {
      return alert(
        "Sabhi hotels points list me already added hain."
      );
    }

    setSearchedUser((previousUser) => ({
      ...previousUser,

      hotelBookingPoints: {
        ...(previousUser.hotelBookingPoints ||
          {}),

        [unusedHotel.id]: 0,
      },
    }));
  };

  const removeHotelPointsRow = (hotelId) => {
    const nextPoints = {
      ...(searchedUser.hotelBookingPoints ||
        {}),
    };

    delete nextPoints[hotelId];

    setSearchedUser((previousUser) => ({
      ...previousUser,
      hotelBookingPoints: nextPoints,
    }));
  };

  // ===============================
  // USER VERIFY
  // ===============================

  const verifyUser = async (userId) => {
    try {
      await updateDoc(
        doc(db, "users", userId),
        {
          verified: true,
        }
      );

      alert("User verified ✅");

      setUsers((previousUsers) =>
        previousUsers.map((user) =>
          user.id === userId
            ? {
                ...user,
                verified: true,
              }
            : user
        )
      );

      if (searchedUser?.id === userId) {
        setSearchedUser((previousUser) => ({
          ...previousUser,
          verified: true,
        }));
      }
    } catch (error) {
      alert(
        "Verify error: " +
          (error?.message || error)
      );
    }
  };

  const unverifyUser = async (userId) => {
    try {
      await updateDoc(
        doc(db, "users", userId),
        {
          verified: false,
        }
      );

      alert("User unverified.");

      setUsers((previousUsers) =>
        previousUsers.map((user) =>
          user.id === userId
            ? {
                ...user,
                verified: false,
              }
            : user
        )
      );

      if (searchedUser?.id === userId) {
        setSearchedUser((previousUser) => ({
          ...previousUser,
          verified: false,
        }));
      }
    } catch (error) {
      alert(
        "Unverify error: " +
          (error?.message || error)
      );
    }
  };

  const deleteUser = async (userId) => {
    const confirmed = window.confirm(
      "User account, profile aur phone index delete karna hai? Booking history preserve rahegi."
    );

    if (!confirmed) return;

    try {
      await httpsCallable(getFunctions(app, 'asia-south1'), 'deleteCustomerAccount')({userId});

      alert(
        "User account aur phone index deleted ✅"
      );

      setUsers((previousUsers) =>
        previousUsers.filter(
          (user) => user.id !== userId
        )
      );

      if (searchedUser?.id === userId) {
        setSearchedUser(null);
      }
    } catch (error) {
      console.error(
        "User delete error:",
        error
      );

      alert(
        "User delete error: " +
          (error?.message || error)
      );
    }
  };

  // ===============================
  // AUTH UI
  // ===============================

  if (!authReady) {
    return (
      <div style={{ padding: 20 }}>
        Checking login...
      </div>
    );
  }

  if (!isAdmin) {
    return (
      <div style={{ padding: 20 }}>
        Admin access required.
      </div>
    );
  }

  if (loading) {
    return (
      <div style={{ padding: 20 }}>
        Loading admin panel...
      </div>
    );
  }

  // ===============================
  // UI
  // ===============================

  return (
    <div
      style={{
        minHeight: "100vh",
        background: "#f6f8fc",
        padding: 20,
      }}
    >
      <div
        style={{
          maxWidth: 1200,
          margin: "0 auto",
        }}
      >
        <h2 style={{ marginTop: 0 }}>
          Admin Panel
        </h2>

        {/* Dashboard Summary */}

        <div
          style={{
            display: "flex",
            gap: 10,
            flexWrap: "wrap",
            marginBottom: 22,
          }}
        >
          <SummaryBox
            label="Total Bookings"
            value={bookings.length}
            background="#eff6ff"
            color="#1d4ed8"
          />

          <SummaryBox
            label="Pending"
            value={pendingCount}
            background="#fff7ed"
            color="#c2410c"
          />

          <SummaryBox
            label="Payment Requests"
            value={paidRequestedCount}
            background="#ecfdf5"
            color="#047857"
          />

          <SummaryBox
            label="Checked In"
            value={checkedInCount}
            background="#f0fdf4"
            color="#15803d"
          />

          <SummaryBox
            label="Free Stays"
            value={freeStayCount}
            background="#faf5ff"
            color="#7c3aed"
          />

          <SummaryBox
            label="Total Users"
            value={users.length}
            background="#f8fafc"
            color="#334155"
          />
        </div>

        {/* Search User */}

        <div
          style={{
            background: "#fff",
            borderRadius: 16,
            padding: 16,
            boxShadow:
              "0 8px 24px rgba(0,0,0,0.06)",
          }}
        >
          <h3 style={{ marginTop: 0 }}>
            Search User by Phone
          </h3>

          <div
            style={{
              display: "flex",
              gap: 10,
              flexWrap: "wrap",
            }}
          >
            <input
              type="tel"
              placeholder="Enter Phone Number"
              value={searchPhone}
              onChange={(event) =>
                setSearchPhone(
                  event.target.value.replace(
                    /\D/g,
                    ""
                  )
                )
              }
              maxLength={10}
              style={{
                padding: 10,
                width: 230,
                maxWidth: "100%",
                border:
                  "1px solid #cbd5e1",
                borderRadius: 10,
              }}
            />

            <button
              onClick={handleSearch}
              style={buttonStyle("#2563eb")}
            >
              Search
            </button>
          </div>
        </div>

        {/* Searched User */}

        {searchedUser && (
          <div
            style={{
              marginTop: 18,
              background: "#fff",
              borderRadius: 16,
              padding: 16,
              boxShadow:
                "0 8px 24px rgba(0,0,0,0.06)",
            }}
          >
            <h3 style={{ marginTop: 0 }}>
              User Details
            </h3>

            <DetailLine
              label="Name"
              value={searchedUser.name || "-"}
            />

            <DetailLine
              label="Phone"
              value={
                searchedUser.phoneNumber ||
                "-"
              }
            />

            <DetailLine
              label="Email"
              value={searchedUser.email || "-"}
            />

            <DetailLine
              label="Referral Phone"
              value={
                searchedUser.referralPhone ||
                "None"
              }
            />

            <div style={{ marginTop: 8 }}>
              <b>Verified:</b>{" "}
              {searchedUser.verified ? (
                <span
                  style={{
                    color: "green",
                    fontWeight: 800,
                  }}
                >
                  Yes ✅
                </span>
              ) : (
                <span
                  style={{
                    color: "red",
                    fontWeight: 800,
                  }}
                >
                  No ❌
                </span>
              )}
            </div>

            {/* Spin Points */}

            <div
              style={{
                marginTop: 18,
                padding: 14,
                borderRadius: 14,
                background: "#faf5ff",
                border:
                  "1px solid #e9d5ff",
              }}
            >
              <h4 style={{ marginTop: 0 }}>
                🎡 Spin Points
              </h4>

              <input
                type="number"
                min="0"
                step="1"
                value={
                  searchedUser.spinPoints || 0
                }
                onChange={(event) =>
                  setSearchedUser(
                    (previousUser) => ({
                      ...previousUser,

                      spinPoints: Math.max(
                        0,
                        Number(
                          event.target.value ||
                            0
                        )
                      ),
                    })
                  )
                }
                style={{
                  width: 100,
                  padding: 9,
                  border:
                    "1px solid #cbd5e1",
                  borderRadius: 9,
                }}
              />
            </div>

            {/* Hotel Points */}

            <div
              style={{
                marginTop: 14,
                padding: 14,
                borderRadius: 14,
                background: "#eff6ff",
                border:
                  "1px solid #bfdbfe",
              }}
            >
              <div
                style={{
                  display: "flex",
                  justifyContent:
                    "space-between",
                  gap: 10,
                  flexWrap: "wrap",
                  alignItems: "center",
                }}
              >
                <h4 style={{ margin: 0 }}>
                  🏨 Hotel Booking Points
                </h4>

                <button
                  onClick={addHotelPointsRow}
                  style={buttonStyle("#2563eb")}
                >
                  Add Hotel
                </button>
              </div>

              {Object.entries(
                searchedUser.hotelBookingPoints ||
                  {}
              ).length === 0 ? (
                <p>
                  Is user ke abhi koi
                  hotel-wise points nahi hain.
                </p>
              ) : (
                Object.entries(
                  searchedUser.hotelBookingPoints ||
                    {}
                ).map(
                  ([hotelId, points]) => (
                    <div
                      key={hotelId}
                      style={{
                        marginTop: 12,
                        padding: 10,
                        background: "#fff",
                        borderRadius: 10,
                        display: "flex",
                        gap: 10,
                        flexWrap: "wrap",
                        alignItems: "center",
                      }}
                    >
                      <div
                        style={{
                          flex: 1,
                          minWidth: 180,
                          fontWeight: 800,
                        }}
                      >
                        {getHotelName(
                          hotelId
                        )}
                      </div>

                      <input
                        type="number"
                        min="0"
                        step="1"
                        value={points}
                        onChange={(event) =>
                          updateSearchedHotelPoints(
                            hotelId,
                            event.target.value
                          )
                        }
                        style={{
                          width: 80,
                          padding: 8,
                          border:
                            "1px solid #cbd5e1",
                          borderRadius: 8,
                        }}
                      />

                      <span
                        style={{
                          fontWeight: 800,
                        }}
                      >
                        /10
                      </span>

                      <button
                        onClick={() =>
                          removeHotelPointsRow(
                            hotelId
                          )
                        }
                        style={buttonStyle(
                          "#ef4444"
                        )}
                      >
                        Remove
                      </button>
                    </div>
                  )
                )
              )}
            </div>

            {/* Save User */}

            <button
              onClick={updateUserRewards}
              disabled={savingUser}
              style={{
                ...buttonStyle("#7c3aed"),
                marginTop: 14,
                opacity: savingUser ? 0.7 : 1,
              }}
            >
              {savingUser
                ? "Saving..."
                : "Save Reward Points"}
            </button>

            <div
              style={{
                marginTop: 14,
                display: "flex",
                gap: 10,
                flexWrap: "wrap",
              }}
            >
              {!searchedUser.verified ? (
                <button
                  onClick={() =>
                    verifyUser(
                      searchedUser.id
                    )
                  }
                  style={buttonStyle("green")}
                >
                  Verify
                </button>
              ) : (
                <button
                  onClick={() =>
                    unverifyUser(
                      searchedUser.id
                    )
                  }
                  style={buttonStyle(
                    "#f59e0b"
                  )}
                >
                  Unverify
                </button>
              )}

              <button
                onClick={() =>
                  deleteUser(
                    searchedUser.id
                  )
                }
                style={buttonStyle("#ef4444")}
              >
                Delete User
              </button>
            </div>
          </div>
        )}

        {/* All Bookings */}

        <div style={{ marginTop: 28 }}>
          <h3>All Bookings</h3>

          {bookings.length === 0 ? (
            <p>No bookings found.</p>
          ) : (
            bookings.map((booking) => {
              const isFree =
                isHotelFreeBooking(
                  booking
                );

              const isCheckedIn =
                booking.checkedIn === true ||
                booking.status ===
                  "checked_in";

              const finalPrice =
                getBookingFinalPrice(
                  booking
                );

              return (
                <div
                  key={booking.id}
                  style={{
                    marginBottom: 14,
                    background: "#fff",
                    padding: 14,
                    borderRadius: 14,
                    boxShadow:
                      "0 5px 18px rgba(0,0,0,0.05)",
                  }}
                >
                  <div>
                    <b
                      style={{
                        fontSize: 17,
                      }}
                    >
                      {booking.name || "-"}
                    </b>

                    <span
                      style={{
                        opacity: 0.8,
                      }}
                    >
                      {" "}
                      |{" "}
                      {booking.phoneNumber ||
                        "-"}
                    </span>
                  </div>

                  <DetailLine
                    label="Arrival"
                    value={`${booking.date || "-"} ${
                      booking.time || "-"
                    }`}
                  />

                  <DetailLine
                    label="Hotel"
                    value={booking.hotel || "-"}
                  />

                  <DetailLine
                    label="Plan"
                    value={`${
                      booking.planTitle || "-"
                    } • ${
                      booking.planHours || "-"
                    } hrs`}
                  />

                  {/* Price */}

                  <div style={{ marginTop: 7 }}>
                    <b>Price:</b>{" "}

                    {isFree ? (
                      <span
                        style={{
                          color: "#0a7",
                          fontWeight: 900,
                        }}
                      >
                        FREE 🎁
                      </span>
                    ) : booking.spinUsed ? (
                      <>
                        <span
                          style={{
                            textDecoration:
                              "line-through",
                            color: "#777",
                            marginRight: 7,
                          }}
                        >
                          ₹
                          {booking.originalPrice ||
                            0}
                        </span>

                        <span
                          style={{
                            color: "#16a34a",
                            fontWeight: 900,
                          }}
                        >
                          ₹{finalPrice}
                        </span>
                      </>
                    ) : (
                      <b>₹{finalPrice}</b>
                    )}
                  </div>

                  {/* Spin */}

                  {booking.spinUsed && (
                    <div
                      style={{
                        marginTop: 9,
                        padding: 10,
                        borderRadius: 10,
                        background: "#f5f3ff",
                        border:
                          "1px solid #ddd6fe",
                      }}
                    >
                      <b
                        style={{
                          color: "#7c3aed",
                        }}
                      >
                        🎡 Spin Offer:{" "}
                        {booking.spinDiscountLabel ||
                          "-"}
                      </b>

                      <div
                        style={{
                          marginTop: 4,
                          fontSize: 13,
                        }}
                      >
                        Original: ₹
                        {booking.originalPrice ||
                          0}{" "}
                        | Discount: ₹
                        {booking.discountAmount ||
                          0}{" "}
                        | Final: ₹
                        {finalPrice}
                      </div>
                    </div>
                  )}

                  {/* Free Stay */}

                  {isFree && (
                    <div
                      style={{
                        marginTop: 9,
                        padding: 10,
                        borderRadius: 10,
                        background: "#ecfff4",
                        border:
                          "1px solid #c9f3d9",
                      }}
                    >
                      <b
                        style={{
                          color: "#0a7",
                        }}
                      >
                        🎁 Hotel Loyalty Free
                        Stay
                      </b>

                      <div
                        style={{
                          marginTop: 4,
                          fontSize: 13,
                        }}
                      >
                        Points:{" "}
                        {booking.hotelPointsBefore ??
                          10}{" "}
                        → -10 ={" "}
                        {booking.hotelPointsAfter ??
                          0}
                      </div>
                    </div>
                  )}

                  <DetailLine
                    label="Referral"
                    value={
                      booking.referralPhone ||
                      "None"
                    }
                  />

                  {/* Reward Status */}

                  <div style={{ marginTop: 7 }}>
                    <b>Guest Booking Point:</b>{" "}

                    {booking.bookingPointGiven ? (
                      <span
                        style={{
                          color: "green",
                          fontWeight: 800,
                        }}
                      >
                        +1 Given ✅
                      </span>
                    ) : isFree && isCheckedIn ? (
                      <span
                        style={{
                          color: "#7c3aed",
                          fontWeight: 800,
                        }}
                      >
                        Not Added — Free Stay
                      </span>
                    ) : (
                      <span
                        style={{
                          color: "#64748b",
                          fontWeight: 700,
                        }}
                      >
                        Not given yet
                      </span>
                    )}
                  </div>

                  <div style={{ marginTop: 7 }}>
                    <b>
                      Referrer Spin Point:
                    </b>{" "}

                    {booking.referrerSpinGiven ? (
                      <span
                        style={{
                          color: "#7c3aed",
                          fontWeight: 800,
                        }}
                      >
                        +1 Given 🎡
                      </span>
                    ) : (
                      <span
                        style={{
                          color: "#64748b",
                          fontWeight: 700,
                        }}
                      >
                        {booking.referralPhone
                          ? "Not given yet"
                          : "No referral"}
                      </span>
                    )}
                  </div>

                  {/* Status */}

                  <div style={{ marginTop: 8 }}>
                    <b>Status:</b>{" "}

                    <span
                      style={{
                        color: isCheckedIn
                          ? "green"
                          : isFree
                          ? "#0a7"
                          : booking.status ===
                            "pending"
                          ? "orange"
                          : booking.status ===
                            "paid_requested"
                          ? "#0a7"
                          : "#2563eb",

                        fontWeight: 800,
                      }}
                    >
                      {isCheckedIn
                        ? "Checked In ✅"
                        : isFree
                        ? "Free Stay Confirmed 🎁"
                        : booking.status || "-"}
                    </span>
                  </div>

                  {/* Booking Buttons */}

                  <div
                    style={{
                      marginTop: 12,
                      display: "flex",
                      gap: 10,
                      flexWrap: "wrap",
                    }}
                  >
                    {booking.status ===
                      "pending" &&
                      !isFree && (
                        <button
                          onClick={() =>
                            confirmBooking(
                              booking
                            )
                          }
                          style={buttonStyle(
                            "#2563eb"
                          )}
                        >
                          Confirm ✅
                        </button>
                      )}

                    {booking.status ===
                      "paid_requested" && (
                      <button
                        onClick={() =>
                          confirmPayment(
                            booking
                          )
                        }
                        style={buttonStyle(
                          "#0a7"
                        )}
                      >
                        Confirm Payment ✅
                      </button>
                    )}

                    <button
                      onClick={() =>
                        deleteBooking(
                          booking.id
                        )
                      }
                      style={buttonStyle(
                        "#ef4444"
                      )}
                    >
                      Delete 🗑
                    </button>
                  </div>
                </div>
              );
            })
          )}
        </div>

        {/* All Users */}

        <div style={{ marginTop: 30 }}>
          <h3>All Users</h3>

          {users.length === 0 ? (
            <p>No users found.</p>
          ) : (
            users.map((user) => {
              const loyaltyHotelCount =
                Object.keys(
                  user.hotelBookingPoints ||
                    {}
                ).length;

              return (
                <div
                  key={user.id}
                  style={{
                    background: "#fff",
                    borderRadius: 12,
                    padding: 12,
                    marginBottom: 10,
                    boxShadow:
                      "0 4px 14px rgba(0,0,0,0.04)",
                  }}
                >
                  <div>
                    <b>{user.name || "-"}</b>

                    <span
                      style={{
                        opacity: 0.8,
                      }}
                    >
                      {" "}
                      |{" "}
                      {user.phoneNumber || "-"}
                    </span>
                  </div>

                  <div
                    style={{
                      marginTop: 5,
                      fontSize: 14,
                    }}
                  >
                    Email: {user.email || "-"}
                  </div>

                  <div
                    style={{
                      marginTop: 5,
                      fontSize: 14,
                    }}
                  >
                    Referral:{" "}
                    {user.referralPhone ||
                      "None"}
                  </div>

                  <div
                    style={{
                      marginTop: 5,
                      fontSize: 14,
                    }}
                  >
                    🎡 Spin Points:{" "}
                    <b>
                      {Number(
                        user.spinPoints || 0
                      )}
                    </b>
                  </div>

                  <div
                    style={{
                      marginTop: 5,
                      fontSize: 14,
                    }}
                  >
                    🏨 Hotels with Loyalty
                    Progress:{" "}
                    <b>{loyaltyHotelCount}</b>
                  </div>

                  <div
                    style={{
                      marginTop: 5,
                    }}
                  >
                    Verified:{" "}

                    {user.verified ? (
                      <span
                        style={{
                          color: "green",
                          fontWeight: 800,
                        }}
                      >
                        Yes ✅
                      </span>
                    ) : (
                      <span
                        style={{
                          color: "red",
                          fontWeight: 800,
                        }}
                      >
                        No ❌
                      </span>
                    )}
                  </div>

                  <div
                    style={{
                      marginTop: 10,
                      display: "flex",
                      gap: 10,
                      flexWrap: "wrap",
                    }}
                  >
                    {!user.verified ? (
                      <button
                        onClick={() =>
                          verifyUser(user.id)
                        }
                        style={buttonStyle(
                          "green"
                        )}
                      >
                        Verify
                      </button>
                    ) : (
                      <button
                        onClick={() =>
                          unverifyUser(user.id)
                        }
                        style={buttonStyle(
                          "#f59e0b"
                        )}
                      >
                        Unverify
                      </button>
                    )}

                    <button
                      onClick={() =>
                        deleteUser(user.id)
                      }
                      style={buttonStyle(
                        "#ef4444"
                      )}
                    >
                      Delete
                    </button>
                  </div>
                </div>
              );
            })
          )}
        </div>
      </div>
    </div>
  );
}

// ===============================
// SMALL COMPONENTS
// ===============================

function SummaryBox({
  label,
  value,
  background,
  color,
}) {
  return (
    <div
      style={{
        minWidth: 140,
        padding: "12px 15px",
        borderRadius: 12,
        background,
        color,
        fontWeight: 800,
      }}
    >
      <div
        style={{
          fontSize: 12,
          opacity: 0.85,
        }}
      >
        {label}
      </div>

      <div
        style={{
          marginTop: 5,
          fontSize: 22,
        }}
      >
        {value}
      </div>
    </div>
  );
}

function DetailLine({ label, value }) {
  return (
    <div style={{ marginTop: 7 }}>
      <b>{label}:</b> {value}
    </div>
  );
}

function buttonStyle(background) {
  return {
    background,
    color: "#fff",
    padding: "8px 12px",
    border: "none",
    borderRadius: 8,
    cursor: "pointer",
    fontWeight: 700,
  };
}

export default AdminPanel;